import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Where a detail page was opened from, carried in router state so its back button
 * returns there instead of to a fixed destination.
 *
 * A fix can be reached from the Fixes board, from a lane list, or from the finding it
 * addresses; a finding can be reached from the findings list or from a fix. "Back" should
 * mean the place you actually came from in each case.
 */
export interface NavigationOrigin {
  /** Path (with query string) to return to. */
  to: string;
  /** Read as "Back to {label}" — e.g. "Fixes", "Findings", "finding". */
  label: string;
}

/** Router state shape: `navigate(to, originState(origin))` / `<Link state={...}>`. */
export interface NavigationOriginState {
  from: NavigationOrigin;
}

/**
 * Router state marking the current page as the origin of the next navigation. Built from
 * the live location, so a link keeps whatever filters or page number are in the URL.
 */
export const useNavigationOrigin = (label: string): NavigationOriginState => {
  const location = useLocation();
  return useMemo(
    () => ({ from: { to: `${location.pathname}${location.search}`, label } }),
    [location.pathname, location.search, label],
  );
};

/**
 * The origin out of router state, or null when there isn't one — a page opened from a
 * pasted URL, a refresh, or a link that predates this convention. Callers fall back to
 * their own default, so nothing depends on the state being there.
 */
export const readNavigationOrigin = (state: unknown): NavigationOrigin | null => {
  const from = (state as NavigationOriginState | null)?.from;
  if (!from || typeof from.to !== 'string' || typeof from.label !== 'string') return null;
  // Only ever navigate within the app: state is attacker-controllable via history entries.
  if (!from.to.startsWith('/') || from.to.startsWith('//')) return null;
  return from;
};

export default useNavigationOrigin;
